import * as React from 'react';
import { useDispatch } from 'react-redux';
import Modal from 'antd/lib/modal';
import Form from 'antd/lib/form';
import Alert from 'antd/lib/alert';
import Text from 'antd/lib/typography/Text';
import { TRY_REQUEST_ISSUE_FIX, TriageViewType, tryRequestIssueFix } from '@/actions/triage';
import { IssueEntity } from '@/types';
import { antGrey } from '@/components/styles/globalStyles';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { TriageIssueCard } from './TriageIssueCard';
import { FixRequestFormValues, useFixRequestForm } from './FixRequestForm';

interface RequestFixModalProps {
    open: boolean;
    onClose: () => void;
    orgId: string;
    appId: string;
    issue: IssueEntity;
    viewType: TriageViewType;
}

export const RequestFixModal: React.FC<RequestFixModalProps> = ({ open, onClose, orgId, appId, issue, viewType }) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm<FixRequestFormValues>();

    const { nextVersion, fields, seedOnOpen, buildFormFieldsValues } = useFixRequestForm(appId);
    // Default the target version to the next release.
    const selectedVersion = Form.useWatch('version', form) ?? nextVersion;

    const isSaving = useIsLoading(TRY_REQUEST_ISSUE_FIX, issue.id);
    const saveError = useError(TRY_REQUEST_ISSUE_FIX, issue.id);
    const saveSucceeded = useUpdated(TRY_REQUEST_ISSUE_FIX, issue.id);

    React.useEffect(() => {
        if (saveSucceeded) {
            form.resetFields();
            onClose();
        }
    }, [saveSucceeded]);

    const onSubmit = (values: FixRequestFormValues) => {
        const version = values.version ?? nextVersion;
        if (!version) {
            return;
        }
        const formFieldsValues = buildFormFieldsValues(values);
        dispatch(tryRequestIssueFix({ orgId, appId, issueId: issue.id, version, formFieldsValues, viewType }));
    };

    return (
        <Modal
            title="Request approval for a fix"
            open={open}
            onCancel={onClose}
            onOk={() => form.submit()}
            okText="Submit request"
            okButtonProps={{ disabled: !selectedVersion }}
            confirmLoading={isSaving}
            afterOpenChange={(isOpen) => {
                if (isOpen) {
                    seedOnOpen(form);
                }
            }}
            destroyOnClose
            width={600}
        >
            <Text color={antGrey.grey8}>
                Choose the issue you&apos;d like to pull in as a fix for this release. You can optionally include
                details about why the fix is needed.
            </Text>
            <Form
                form={form}
                layout="vertical"
                onFinish={onSubmit}
                requiredMark
                preserve={false}
                style={{ marginTop: 24 }}
            >
                {saveError && <Alert type="error" message={saveError} showIcon style={{ marginBottom: 16 }} />}
                {fields}
                <Form.Item label="Associated issue" required>
                    <TriageIssueCard issue={issue} orgId={orgId} preview viewType={viewType} />
                </Form.Item>
            </Form>
        </Modal>
    );
};
